<template>
  <div
    class="Step0Form"
    :class="{ 'padding-top': extra?.addPaddingTop }"
  >
    <!-- Fecha de inicio -->
    <DatePickerComponent
      id="start-date"
      :label="t('rater.common.modify-search-view.start-date')"
      v-model="repricingAnswers.startDate"
      :min-date="minDateInit"
      :max-date="maxDateInit"
      :rules="[validationRules.required]"
    />

    <DatePickerComponent
      v-if="
        repricingAnswers.stayAbroadType.toString() == props.configurations.insuranceTypes.student.id
      "
      id="end-date"
      :label="t('rater.common.modify-search-view.end-date')"
      v-model="repricingAnswers.endDate"
      :min-date="minDateEnd"
      :max-date="maxDateEnd"
      :rules="[validationRules.required, validateEndDate]"
    />

    <template
      v-for="({ isHolder, userDetails, addressDetails }, index) in repricingAnswers.insureds"
      :key="index"
    >
      <PostalCodeComponent
        :id="`postal-code-${index}`"
        :label="postalCodeStatement(isHolder, index)"
        v-model="addressDetails.postalCode"
        :placeholder="t('rater.common.more-info.postal-code.placeholder')"
        :rules="postalCodeRules"
        @serviceLoading="isServiceLoading[0] = $event"
      />

      <FormField :statement="birthDateStatement(isHolder, index)">
        <BirthDatePickerComponent
          :id="`birthDate-${index}`"
          v-model="userDetails.birthDate"
          :rules="
            isHolder
              ? [
                  validationRules.required,
                  validationRules.tooOldGreaterOrEqual(maxAgeHolder),
                  validationRules.tooYoung(minAgeHolder)
                ]
              : [validationRules.required, validationRules.tooOldGreaterOrEqual(maxAgeInsured)]
          "
        />
      </FormField>
    </template>

    <div class="actions">
      <BaseButton
        @click="simulateRepricing"
        :ariaDisabled="!isValidForm || !changeData"
      >
        {{ t('rater.base.submit') }}
      </BaseButton>

      <template v-if="askToCalculateInsurance && price && !changeData">
        <v-divider class="divider-blue" />

        <TotalAmountComponent
          :label="t('hiring.common.step-0.price-updated-label')"
          :amount="price"
          :currency="paymentUnits"
          :period="`/${t(
            repricingAnswers.stayAbroadType.toString() ==
              props.configurations.insuranceTypes.student.id
              ? 'common.periods.stay'
              : 'common.periods.annual'
          )}`"
        />

        <BaseButton @click="repricing">
          {{ t('hiring.common.step-0.price-updated-button') }}
        </BaseButton>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import TotalAmountComponent from '@/modules/common/hiring/components/TotalAmountComponent.vue'
  import { useRepricing } from '@/modules/common/hiring/composables/useRepricing'
  import BirthDatePickerComponent from '@/modules/core/components/formFields/BirthDatePickerComponent.vue'
  import PostalCodeComponent from '@/modules/core/components/formFields/PostalCodeComponent.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import type { FormAnswersType } from '@/modules/core/models/types'
  import { getBranchMaxAge } from '@/modules/core/utils/BranchUtils'
  import type ForeignHealthConfigurations from '@/modules/foreign-health/common/models/APP/ForeignHealthConfigurations'
  import type ForeignHealthInsurance from '@/modules/foreign-health/common/models/APP/ForeignHealthInsurance'
  import { repeat } from '@/utils/array.utils'
  import { addDaysToDate, addMonthsToDate } from '@/utils/date.utils'
  import { computed, onBeforeMount, ref, watch, type Ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ForeignHealthHiringAnswers from '../../models/APP/ForeignHealthHiringAnswers'
  import { useForeignHealthStore } from '../../stores'

  const { t } = useI18n()
  const { required, tooOld, tooOldGreaterOrEqual, tooYoung, isNotFromNavarra } = useRules()
  const foreignHealthStore = useForeignHealthStore()
  const errorManager = useErrorManagerComposable()

  interface PropsInterface {
    nextForm: string[]
    answers: FormAnswersType
    configurations: ForeignHealthConfigurations
    isValidForm: boolean
    showTitle?: boolean
    extra?: {
      addPaddingTop?: boolean
    }
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const emit = defineEmits(['repricing'])

  const postalCodeRules = computed(() => {
    return [required, isNotFromNavarra]
  })

  const repricingAnswers = ref(
    new ForeignHealthHiringAnswers(props.answers)
  ) as Ref<ForeignHealthHiringAnswers>
  const {
    price,
    changeData,
    paymentUnits,
    askToCalculateInsurance,
    initializeCommon,
    simulateRepricingCommon,
    repricingCommon
  } = useRepricing<ForeignHealthHiringAnswers, ForeignHealthInsurance>(
    repricingAnswers,
    props.configurations
  )

  const validationRules = {
    required,
    tooOld: (value: string) =>
      tooOld(
        value,
        props.configurations.hiringMaxAges!.insured.max,
        new Date(),
        `common.validations.max-age-${getBranchMaxAge()}`
      ),
    tooOldGreaterOrEqual: (maxAge: number) => (value: string) =>
      tooOldGreaterOrEqual(value, maxAge, `common.validations.max-age-${getBranchMaxAge()}`),
    tooYoung: (minAge: number) => (value: string) => tooYoung(value, minAge, new Date())
  }

  const isServiceLoading = ref<boolean[]>(repeat(repricingAnswers.value.insureds.length, false))
  const minDateInit = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  })
  const maxDateInit = computed(() =>
    props.configurations.maxDays ? addDaysToDate(props.configurations.maxDays) : null
  )
  const minDateEnd = computed(() => calculateMaxDate(repricingAnswers.value.startDate, 'min'))
  const maxDateEnd = computed(() => calculateMaxDate(repricingAnswers.value.startDate, 'max'))

  function calculateMaxDate(startDate: Date | null, dateOption: 'min' | 'max'): Date {
    let innerStartDate = startDate

    if (!innerStartDate) {
      innerStartDate = new Date()
      innerStartDate.setHours(0, 0, 0, 0)
    }

    if (!props.configurations.hiringEndDateStudent) {
      return innerStartDate
    }
    if (props.configurations.hiringEndDateStudent.time === 'month') {
      return addMonthsToDate(
        Number(props.configurations.hiringEndDateStudent[dateOption]),
        addDaysToDate(-1, innerStartDate)
      )
    }
    return addDaysToDate(
      Number(props.configurations.hiringEndDateStudent[dateOption]),
      innerStartDate
    )
  }

  const maxAgeHolder = computed(() => {
    const index =
      repricingAnswers.value.holder.role === UserRoleEnum.HOLDER_INSURED
        ? 'holderInsured'
        : 'holderNotInsured'

    return props.configurations.hiringMaxAges![index].max
  })

  const minAgeHolder = computed(() => {
    const index =
      repricingAnswers.value.holder.role === UserRoleEnum.HOLDER_INSURED
        ? 'holderInsured'
        : 'holderNotInsured'

    return props.configurations.hiringMaxAges![index].min
  })

  const maxAgeInsured = computed(() => {
    const index = 'insured'
    return props.configurations.hiringMaxAges![index].max
  })

  function postalCodeStatement(isHolder: boolean, index: number) {
    const i18nTag = isHolder ? 'holder' : 'not-holder'
    const insuredCount = props.answers.insureds.length > 1 ? index + 1 : null
    return t(`rater.common.more-info.postal-code.span-list.${i18nTag}`, { index: insuredCount })
  }

  function birthDateStatement(isHolder: boolean, index: number) {
    const i18nTag = isHolder ? 'holder' : 'not-holder'
    const insuredCount = props.answers.insureds.length > 1 ? index + 1 : null
    return t(`rater.common.more-info.birth-date.span-list.${i18nTag}`, { index: insuredCount })
  }

  async function simulateRepricing() {
    try {
      await simulateRepricingCommon(foreignHealthStore.getNewRaterPrice)
      setTimeout(() => {
        const modalElement = document.getElementsByClassName('modal-panel')?.[0]
        if (modalElement) {
          modalElement.scrollTo({
            top: modalElement.scrollHeight,
            behavior: 'smooth'
          })
        }
      }, 100)
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  async function repricing() {
    try {
      await repricingCommon(foreignHealthStore.getNewRaterPrice, foreignHealthStore.hireProduct) // ver si puedo evitar que se llame a raterprice 2 veces
      emit('repricing')
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  function validateEndDate(value: Date | string | null): true | string {
    if (!repricingAnswers.value.startDate || !value) {
      return t('rater.health-foreign.duration.min-range-error')
    }

    // Convertir el valor a un formato válido para Date
    const parsedValue = typeof value === 'string' ? value.split('/').reverse().join('-') : value
    const endDate = parsedValue instanceof Date ? parsedValue : new Date(parsedValue)

    if (isNaN(endDate.getTime())) {
      return t('rater.health-foreign.duration.min-range-error')
    }
    endDate.setHours(0, 0, 0, 0)

    const minEnd = calculateMaxDate(repricingAnswers.value.startDate, 'min')
    const maxEnd = calculateMaxDate(repricingAnswers.value.startDate, 'max')

    if (endDate < minEnd) {
      return t('rater.health-foreign.duration.min-range-error')
    }
    if (endDate > maxEnd) {
      return t('rater.health-foreign.duration.max-range-error')
    }
    return true
  }

  watch(
    () => [repricingAnswers.value],
    () => {
      changeData.value = true
    },
    { deep: true }
  )

  onBeforeMount(() => {
    initializeCommon(repricingAnswers.value)
  })
</script>

<style lang="scss" scoped>
  .Step0Form {
    width: $size-screen-w;
    max-width: $size-375;
    padding: 0 $spacing-l;
    margin: auto;

    .gl_form-title {
      font-size: $font-size-20;
      margin-bottom: $spacing-2xl;
    }
  }

  .padding-top {
    padding-top: $spacing-xl;
  }
</style>
